import { useMemo, useContext } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";

import { ContextMenu } from "../../ContextMenu";
import { BaseTableData, TableColumn } from "../types";
import { DEFAULT_MIN_WIDTH } from "../constants";
import { createCellRenderer } from "../utils";
import { TableV2GridContext } from "../grid-store";

import { cn } from "@/lib/utils";

interface UseColumnDefsProps<T extends BaseTableData> {
  columns: TableColumn<T>[];
  enableSorting: boolean;
  enableResizing: boolean;
  enableFiltering?: boolean;
  suppressHeaderMenuButton?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contextMenuItems?: any[];
}

export const useColumnDefs = <T extends BaseTableData>({
  columns,
  enableSorting,
  enableResizing,
  enableFiltering,
  suppressHeaderMenuButton,
  contextMenuItems,
}: UseColumnDefsProps<T>) => {
  const gridContext = useContext(TableV2GridContext);

  const columnDefs = useMemo<ColDef<T>[]>(
    () =>
      columns.map(column => ({
        ...column,
        suppressHeaderMenuButton,
        headerName: column.header,
        colId: `col_${column.id}`,
        flex: column.width || column.minWidth || column.maxWidth ? undefined : 1,
        width: column.width,
        hide: column.hide || gridContext?.tableStore?.isColumnHidden(column.name),
        minWidth: column.minWidth || DEFAULT_MIN_WIDTH,
        maxWidth: column.maxWidth,
        autoHeight: false,
        sortable: column.sortable !== undefined ? column.sortable : enableSorting,
        resizable: column.resizable !== undefined ? column.resizable : enableResizing,
        filter: column.filter !== undefined ? column.filter : enableFiltering,
        cellClass: cn("flex items-center group/Cell", column.cellClass),
        headerClass: column.headerClass,
        suppressMovable: column.suppressMovable,
        cellRenderer:
          typeof column.cellRenderer === "string"
            ? column.cellRenderer
            : createCellRenderer(column, contextMenuItems),
        cellRendererParams: {
          ...column.cellRendererParams,
          innerRenderer: (params: ICellRendererParams<T>) => (
            <ContextMenu<T>
              data={params.data!}
              modal={false}
              className="flex h-full w-full items-center px-3 leading-none"
              isDisabled={!contextMenuItems || contextMenuItems.length === 0}
              contextMenuItems={contextMenuItems}
            >
              {column.cellRendererParams?.innerRenderer?.(params)}
            </ContextMenu>
          ),
        },
      })),
    [
      columns,
      enableSorting,
      enableResizing,
      enableFiltering,
      suppressHeaderMenuButton,
      contextMenuItems,
      gridContext?.tableStore, // This will trigger re-render when columns are hidden/shown
    ],
  );

  return { columnDefs };
};
